// FILENAME: frontend/src/components/SelectionSummaryCard.jsx
import React, { useMemo } from 'react';
import { Card, CardHeader, CardContent, Box, Alert, Skeleton, Chip } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import useApi from '../hooks/useApi';
import { isSelectionActive } from '../utils/dataNormalizer';
import { isSelectionCoverageEligible } from '../utils/enrollmentCoverage';
import CustomLegend from './CustomLegend';
import AnimatedChart from './AnimatedChart';

const formatProgramTitle = (programName) => {
  if (programName === 'Digital Ad Sales') return 'DigAd Sales';
  if (programName === 'Digital Ad Service') return 'DigAd Service';
  return programName;
};

const SELECTION_COLORS = {
    'With Selections': 'rgba(75, 192, 192, 0.6)',
    'Without Selections': 'rgba(255, 99, 132, 0.6)',
};

export const buildSelectionCoverageChartData = (data) => {
    if (!Array.isArray(data)) return [];

    const enrolledDealers = data.filter(isSelectionCoverageEligible);

    const withSelections = enrolledDealers.filter(
        (dealer) => Array.isArray(dealer.productSelections) && dealer.productSelections.some(isSelectionActive)
    ).length;
    const withoutSelections = enrolledDealers.length - withSelections;

    return [
        { id: 0, value: withSelections, label: 'With Selections', color: SELECTION_COLORS['With Selections'] },
        { id: 1, value: withoutSelections, label: 'Without Selections', color: SELECTION_COLORS['Without Selections'] }
    ];
};

const SelectionSummaryCard = ({ programName, onChartClick }) => {
    const { useEnrollmentData } = useApi();
    const { data, isLoading, error } = useEnrollmentData(programName);
    const displayTitle = formatProgramTitle(programName);
    const isDigAd = displayTitle.startsWith('DigAd');

    const chartData = useMemo(() => {
        return buildSelectionCoverageChartData(data);
    }, [data]);

    const totalDealers = useMemo(() => chartData.reduce((sum, item) => sum + item.value, 0), [chartData]);

    const handleChartClick = (event, item) => {
        if (onChartClick && item && chartData[item.dataIndex]) {
            onChartClick({ programName, selectionStatus: chartData[item.dataIndex].label });
        }
    };
    
    const handleLegendClick = (label) => {
        if (onChartClick) {
            onChartClick({ programName, selectionStatus: label });
        }
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader
              title={displayTitle}
              subheader="Selection coverage (enrolled only)"
              titleTypographyProps={{
                noWrap: true,
                sx: {
                  fontSize: isDigAd ? '1rem' : '1.05rem',
                },
              }}
              action={(
                <Chip
                  size="small"
                  variant="outlined"
                  color="primary"
                  label={`${totalDealers} enrolled`}
                  sx={{ mt: 0.5 }}
                />
              )}
            />
            <CardContent>
                {isLoading && (
                     <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                            <Skeleton variant="circular" width={140} height={140} />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="55%" />
                        </Box>
                    </>
                )}
                {error && <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{error.message}</Alert>}
                {data && !isLoading && (
                     <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                            <AnimatedChart animationKey={`${programName}:${chartData.map((d) => d.value).join('-')}`}>
                                <PieChart
                                    series={[{ data: chartData, innerRadius: 30 }]}
                                    width={150}
                                    height={150}
                                    legend={{ hidden: true }}
                                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                                    onClick={handleChartClick}
                                />
                            </AnimatedChart>
                        </Box>
                        <CustomLegend data={chartData} onLegendClick={handleLegendClick} />
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default SelectionSummaryCard;
